import React from "react";
import { Leaf, ChevronRight, Fuel, Shield, Activity, Wrench, Monitor, Flame } from "lucide-react";
import { trackFunnelEvent } from "../../utils/analytics";

// MOBILSAFE Logo from official website
const MOBILSAFE_LOGO = "/logo-mobilsafe.png";

const FeatureCard = ({ icon, title, description, color, onClick, areaId, pulseDelay }) => (
  <button 
    onClick={onClick}
    className="bg-emerald-50/60 border border-emerald-200/60 rounded-2xl p-6 hover:bg-emerald-100/60 hover:border-emerald-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-emerald-500/10 transition-all group text-left w-full"
    style={{ animation: "pulse-card 6s ease-in-out infinite", animationDelay: pulseDelay }}
    data-testid={`area-card-${areaId}`}
  >
    <div className={`w-12 h-12 bg-gradient-to-br ${color} rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>{icon}</div>
    <h4 className="font-bold text-lg mb-2 uppercase text-slate-800">{title}</h4>
    <p className="text-sm text-slate-500">{description}</p>
    <div className="mt-3 text-xs text-emerald-600 flex items-center gap-1 font-semibold">
      + Ahorro <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
    </div>
  </button>
);

const Landing = ({ onStart, onAdmin, onCAES, onAreaClick }) => {
  return (
    <div className="min-h-screen bg-white text-slate-800 overflow-hidden">
      <div className="relative">
        <div className="absolute inset-0 opacity-[0.025]">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2310b981' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <header className="relative z-10 px-6 py-6 border-b border-slate-200">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img 
                src={MOBILSAFE_LOGO} 
                alt="MOBILSAFE" 
                className="h-10 md:h-12"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Leaf className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-black tracking-tight text-slate-900">MOBILSAFE</h1>
                  <p className="text-xs text-emerald-600 tracking-widest">FLEET TECHNOLOGY</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={onCAES} className="px-6 py-2 bg-gradient-to-r from-[#ec561c] to-[#d44a15] hover:from-[#d44a15] hover:to-[#b8400f] text-white rounded-full font-semibold transition-all uppercase text-sm tracking-wide shadow-lg shadow-orange-500/20" data-testid="header-caes-btn">
                Tu Ingreso CAES
              </button>
            </div>
          </div>
        </header>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-emerald-100 border border-emerald-300 px-4 py-2 rounded-full">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span className="text-emerald-700 text-sm font-medium uppercase tracking-wide">Tu Análisis de Flota</span>
              </div>
              
              <h2 className="text-5xl lg:text-6xl font-black leading-tight uppercase text-slate-900">
                Reduce costes y <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-green-500">emisiones CO₂</span>
              </h2>
              
              <p className="text-xl text-slate-500 leading-relaxed">
                Calcula en segundos el impacto sostenible de MOBILSAFE en tu flota. 
                Ahorro de combustible, reducción de emisiones y certificados CAES monetizables.
              </p>

              <div className="flex flex-wrap gap-4">
                <button onClick={() => { trackFunnelEvent("landing", "cta_empezar_analisis"); onStart && onStart(); }} className="group px-8 py-4 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white rounded-xl font-bold text-lg shadow-xl shadow-emerald-500/30 transition-all transform hover:scale-105 flex items-center gap-3 uppercase tracking-wide" data-testid="hero-start-btn" title="Descubre en 2 minutos dónde pierde dinero tu flota y cuánto puedes recuperar">
                  <Leaf className="w-5 h-5" />
                  Tu Análisis de Flota
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                
                <button onClick={onCAES} className="group px-8 py-4 bg-gradient-to-r from-[#ec561c] to-[#d44a15] hover:from-[#d44a15] hover:to-[#b8400f] text-white rounded-xl font-bold text-lg shadow-xl shadow-orange-500/30 transition-all transform hover:scale-105 flex items-center gap-3 uppercase tracking-wide" data-testid="hero-caes-btn" title="Calcula tu potencial de ingresos CAES en 30 segundos">
                  <Flame className="w-5 h-5" />
                  TU INGRESO CAES
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-6 pt-8 border-t border-slate-200">
                <div><p className="text-3xl font-black text-emerald-600">+500</p><p className="text-sm text-slate-500 uppercase">Flotas analizadas</p></div>
                <div><p className="text-3xl font-black text-emerald-600">45K</p><p className="text-sm text-slate-500 uppercase">Ton CO₂ evitadas</p></div>
                <div><p className="text-3xl font-black text-emerald-600">12M€</p><p className="text-sm text-slate-500 uppercase">Ahorros generados</p></div>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <FeatureCard 
                icon={<Shield className="w-6 h-6 text-white" />} 
                title="Seguridad" 
                description="Reduce accidentes y riesgo" 
                color="from-red-500 to-rose-600" 
                areaId="seguridad"
                pulseDelay="0s"
                onClick={() => onAreaClick && onAreaClick('seguridad')}
              />
              <FeatureCard 
                icon={<Fuel className="w-6 h-6 text-white" />} 
                title="Combustible" 
                description="Hasta 8% menos consumo" 
                color="from-amber-500 to-orange-600" 
                areaId="combustible"
                pulseDelay="1s"
                onClick={() => onAreaClick && onAreaClick('combustible')}
              />
              <FeatureCard 
                icon={<Activity className="w-6 h-6 text-white" />} 
                title="Operativa" 
                description="Cumple plazos y descubre improductividades" 
                color="from-blue-500 to-indigo-600" 
                areaId="operativa"
                pulseDelay="2s"
                onClick={() => onAreaClick && onAreaClick('operativa')}
              />
              <FeatureCard 
                icon={<Wrench className="w-6 h-6 text-white" />} 
                title="Mantenimiento" 
                description="Anticipa averías" 
                color="from-purple-500 to-violet-600" 
                areaId="mantenimiento"
                pulseDelay="3s"
                onClick={() => onAreaClick && onAreaClick('mantenimiento')}
              />
              <FeatureCard 
                icon={<Monitor className="w-6 h-6 text-white" />} 
                title="TIC / Datos" 
                description="Integra fácil" 
                color="from-cyan-500 to-teal-600" 
                areaId="tic"
                pulseDelay="4s"
                onClick={() => onAreaClick && onAreaClick('tic')}
              />
              <FeatureCard 
                icon={<Leaf className="w-6 h-6 text-white" />} 
                title="Sostenibilidad" 
                description="Reduce CO₂ e ingresa dinero" 
                color="from-emerald-500 to-green-600" 
                areaId="sostenibilidad"
                pulseDelay="5s"
                onClick={() => onAreaClick && onAreaClick('sostenibilidad')}
              />
            </div>
          </div>
        </div>
      </div>

      <footer className="relative z-10 border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <img 
            src={MOBILSAFE_LOGO} 
            alt="MOBILSAFE" 
            className="h-8 mx-auto mb-4 opacity-60"
          />
          <div className="flex flex-wrap justify-center gap-4 mb-4 text-sm">
            <a 
              href="https://www.mobilsafe.es/condiciones-de-uso-y-privacidad/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-emerald-600 transition-all"
            >
              Condiciones de uso y privacidad
            </a>
            <span className="text-slate-300">|</span>
            <a 
              href="https://www.mobilsafe.es/politica-de-cookies/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-emerald-600 transition-all"
            >
              Política de cookies
            </a>
          </div>
          <button 
            onClick={onAdmin} 
            className="text-slate-400 hover:text-emerald-600 text-sm transition-all cursor-pointer"
            data-testid="footer-admin-link"
          >
            © 2026 MOBILSAFE Fleet Technology
          </button>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
